import React, { useState, useEffect } from 'react';
import ProctoringSystem from './ProctoringSystem';
import axios from 'axios';

import API_BASE_URL from '../../Config/config';

const PremiumInterviewView = ({ userId, technology, experience, interviewType }) => {
  const [interviewId, setInterviewId] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [userAnswer, setUserAnswer] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);
  const [isStarted, setIsStarted] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [thinkingStartTime, setThinkingStartTime] = useState(null);
  const [apiBaseUrl] = useState(`${API_BASE_URL}/premium`);

  const startInterview = async () => {
    try {
      const response = await axios.post(`${apiBaseUrl}/start`, {
        userId, technology, experience, interviewType
      });
      if (response.data.ok) {
        setInterviewId(response.data.interviewId);
        setCurrentQuestion(response.data.question);
        setIsStarted(true);
        setThinkingStartTime(Date.now());
        document.documentElement.requestFullscreen();
      }
    } catch (error) {
      console.error('Failed to start interview', error);
    }
  };

  const submitAnswer = async () => {
    const duration = Math.floor((Date.now() - thinkingStartTime) / 1000);
    try {
      const response = await axios.post(`${apiBaseUrl}/submit`, {
        interviewId,
        answer: userAnswer,
        thinkingTime: duration, // Simplification
        duration: duration
      });
      
      if (response.data.completed) {
        setIsCompleted(true);
      } else {
        setCurrentQuestion(response.data.question);
        setUserAnswer('');
        setThinkingStartTime(Date.now());
      }
    } catch (error) {
      console.error('Failed to submit answer', error);
    }
  };

  const handleViolation = async (type, details) => {
    setWarnings(prev => [...prev, { type, timestamp: new Date() }]);
    console.warn('Violation detected:', type, details);
    
    // Log to backend
    if (interviewId) {
      await axios.post(`${apiBaseUrl}/integrity`, {
        interviewId,
        violationType: type,
        details: details
      });
    }

    if (warnings.length >= 3) {
       alert("Interview terminated due to multiple violations.");
       setIsCompleted(true);
    }
  };

  if (!isStarted) {
    return (
      <div className="interview-intro p-8 max-w-2xl mx-auto">
        <h2 className="text-2xl font-bold mb-4">Premium AI Interview: {technology}</h2>
        <p className="mb-4">This interview will use proctoring. Please ensure you are in a quiet room, your camera is on, and you stay in fullscreen mode.</p>
        <button 
          onClick={startInterview}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          I Accept, Start Interview
        </button>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="interview-completed p-8 max-w-2xl mx-auto text-center">
        <h2 className="text-3xl font-bold mb-4">Interview Completed!</h2>
        <p>Your report is being generated. You can view it in your dashboard shortly.</p>
        <button className="mt-6 bg-blue-600 text-white px-6 py-2 rounded-lg">Go to Dashboard</button>
      </div>
    );
  }

  return (
    <div className="interview-room p-8 max-w-4xl mx-auto flex flex-col h-screen">
      <ProctoringSystem isActive={!isCompleted} onViolation={handleViolation} onStatusChange={()=>{}} />
      
      <div className="flex-grow flex flex-col justify-center">
        <div className="ai-question bg-gray-100 p-6 rounded-xl mb-8 shadow-sm">
          <p className="text-xl font-medium text-gray-800">{currentQuestion}</p>
        </div>

        <textarea
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-full h-48 p-4 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none resize-none"
          placeholder="Type your answer here..."
        />
        
        <div className="flex justify-end mt-6">
          <button 
            onClick={submitAnswer}
            disabled={!userAnswer.trim()}
            className="bg-green-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-green-700 disabled:opacity-50 transition"
          >
            Submit Answer
          </button>
        </div>
      </div>

      <div className="warnings-display fixed top-4 right-4">
        {warnings.length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative shadow-md">
            <strong className="font-bold">Warnings: {warnings.length}/3</strong>
          </div>
        )}
      </div>
    </div>
  );
};

export default PremiumInterviewView;
